import tw, { styled } from 'twin.macro';
import { variants } from '@/components/Link/Link.styles';
import { StyledContainer as StyledStarContainer } from '@/components/StarRating/StarRating.styles';
import KontentImage from '../KontentImage/KontentImage';

export const StyledContainer = styled.div`
  ${tw`container`}
  ${tw`container-stack-context`}
  ${tw`lg:grid lg:grid-cols-2 lg:grid-rows-1 lg:gap-x-4`}
  ${tw`!max-w-[600px] lg:!max-w-lg`}
`;

export const StyledFormLead = styled.div`
  ${tw`container`}
  ${tw`container-stack-context`}
  ${tw`px-2 md:p-0 lg:py-0 lg:px-0`}
  ${tw`mx-0`}
  ${tw`row-start-1 col-start-1`}
  ${tw`!max-w-[600px] lg:!max-w-lg`}

  ${StyledStarContainer} {
    ${tw`mb-3`}
  }
`;

export const StyledHeadline = styled.h1`
  ${tw`mb-3`}
  ${tw`text-display-xl`}
  ${tw`font-poppins font-semi-bold`}
`;

export const StyledSection = styled.section<{
  theme: Queries.kontent_item_form_container_elements_theme_enum;
}>`
  ${({ theme }) => theme === 'dark' && tw`bg-primary-p1`}

  ${StyledFormLead} {
    ${({ theme }) =>
      theme === 'light' ? tw`text-neutrals-n8` : tw`bg-primary-p1 text-neutrals-n1`}
  }

  ${StyledHeadline} {
    ${({ theme }) => (theme === 'light' ? tw`text-secondary-s1` : tw`text-neutrals-n1`)}
  }
`;

export const StyledFormLeadHeader = styled.div<{ hideOnMobile: boolean }>`
  ${({ hideOnMobile }) => (hideOnMobile ? tw`hidden lg:block` : tw`block`)}
`;

export const StyledFormLeadHeaderMobile = styled(StyledFormLead)`
  ${tw`lg:hidden`}
`;

export const StyledRichText = styled.div`
  ${tw`font-inter font-regular`}
  ${tw`text-body-m`}
  p:not(:last-child) {
    ${tw`mb-3`}
  }

  ul {
    li {
      ${tw`bg-[url("/svg/check-circle-pricing.svg")] bg-no-repeat bg-left-top`}
      ${tw`pl-[36px]`}
      ${tw`mb-[16px]`}
      ${tw`list-outside list-none`}
    }
  }
`;

export const StyledCompanyLogos = styled.ul`
  ${tw`grid grid-cols-2 justify-start md:grid-cols-4`}
  ${tw`gap-[20px]`}
  ${tw`my-5`}
`;

export const StyledCompanyLogoItem = styled.li`
  ${tw`list-none`}
`;

export const StyledKontentImage = styled(KontentImage)`
  ${tw`max-h-[72px]`}
  ${tw`w-full max-w-[180px]`}
  ${tw`mx-auto`};
`;

const calcScrollDownForMoreSpacing = (value: 'none' | 'small' | 'medium' | 'large') => {
  switch (value) {
    case 'small':
      return tw`pt-2 md:pt-4`;
    case 'medium':
      return tw`pt-4 md:pt-8`;
    case 'large':
      return tw`pt-8 md:pt-[128px]`;
    case 'none':
      return tw`pt-0`;
  }
};

export const ScrollDownForMoreWrapper = styled('div', {
  shouldForwardProp: (propName) => propName !== 'spacing',
})<{ spacing: 'none' | 'small' | 'medium' | 'large' }>`
  ${({ spacing }) => calcScrollDownForMoreSpacing(spacing)}
`;

export const StyledFormHeadline = styled.h2`
  ${tw`text-mobile-heading-m md:text-heading-m`}
  ${tw`text-center`}
  ${tw`pb-3`}
  ${tw`text-secondary-s1`}
  ${tw`font-poppins font-semi-bold`}
`;

export const StyledFormLogo = styled.div<{ showOnMobile: boolean }>`
  ${tw`mb-2 mx-2 md:mb-4 md:mx-0 lg:mb-2`}
  ${tw`lg:row-end-1`}
  ${({ showOnMobile }) => !showOnMobile && tw`mx-0 lg:mb-[42px]`}
`;

export const StyledFormWrap = styled.div`
  ${tw`bg-neutrals-n1 md:bg-[transparent]`}
  ${tw`mt-[26px] mb-4 lg:my-0`}
  ${tw`lg:row-span-2`}
`;

export const StyledFormInner = styled.div<{ hasDropShadow?: boolean }>`
  ${tw`md:max-w-[600px] lg:max-w-[464px]`}
  ${tw`px-2 lg:px-0`}
  ${tw`md:mx-auto`}
  ${tw`md:bg-ui-colours-ui1`}
  ${tw`md:rounded-xl`}
  ${({ hasDropShadow }) => hasDropShadow && tw`md:shadow-large md:border-4 md:border-primary-p1`}
`;

export const StyledFormContainer = styled.div`
  ${tw`px-0 md:px-4 pt-4 pb-2`}

  ${StyledStarContainer} {
    ${tw`mb-2`}
    ${tw`justify-center`}
  }
`;

export const StyledButtonList = styled.ul`
  ${tw`mt-[26px] mb-3`}
`;

export const StyledButtonListItem = styled.li`
  ${tw`md:inline-block`}
  ${tw`last:mr-0 mb-1`}
`;

export const StyledButton = styled.div<{ active: boolean }>`
  ${variants['primary_small']}
  ${tw`mx-[10px] mb-1 md:mb-0 first:ml-0 hover:first:ml-0`}
  ${({ active }) =>
    !active && [
      tw`text-secondary-s1`,
      tw`bg-ui-colours-ui3`,
      tw`border-ui-colours-ui3`,
      tw`hover:bg-ui-colours-ui3`,
      tw`hover:text-ui-colours-ui2`,
    ]}
`;
